import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { environment } from '../../environments/environment';

import { CustomIconControl } from '../map/custom-controls/custom-icon.control';
import mapboxgl, { FullscreenControl, LngLatLike } from 'mapbox-gl';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { onSelectLayer, setSavedMapLayerStyle } from 'src/utils/constants';

@Injectable({
  providedIn: 'root',
})
export class SatMapService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);

  map: mapboxgl.Map;
  localStorageId = 'sat_map_service_map_layer';
  style = 'mapbox://styles/' + environment.mapbox.layers[0].style;
  zoom = 4;
  coordinates: [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
  ] = [
    [-22.457882, 66.446027],
    [43.119133, 66.446027],
    [43.119133, 33.035372],
    [-22.457882, 33.035372],
  ];
  bounds = [-22.457882, 43.119133, 33.035372, 66.446027];
  mapLayers = environment.mapbox.layers;

  satData: SatelliteData;
  language: string;

  timeStampControl: TimeStampControl;
  satLayers: Array<mapboxgl.AnyLayer> = [];
  mapLoaded = new BehaviorSubject<boolean>(false);

  constructor() {
    mapboxgl.accessToken = environment.mapbox.accessToken;
    setSavedMapLayerStyle.bind(
      this,
      this.localStorageId,
      this.mapLayers,
      (style: string) => {
        this.style = style;
      }
    )();
  }

  buildMap(center: LngLatLike, satType: string, hours: number): void {
    this.map = new mapboxgl.Map({
      container: 'map',
      style: this.style,
      zoom: this.zoom,
      center,
      scrollZoom: true,
      cooperativeGestures: true,
    });
    //enableCommandWheelZoom(this.map);

    this.map.on('load', () => {
      this.map.addControl(new FullscreenControl());
      this.addSatLayers();
      this.setSelectLayerControl();
      this.mapLoaded.next(true);
      this.map.setMaxBounds(
        new mapboxgl.LngLatBounds(
          [-22.457882, 66.446027],
          [43.119133, 33.035372]
        )
      );
      this.map.setMaxBounds(null);

      /*
      [-22.457882, 66.446027],
    [43.119133, 66.446027],
    [43.119133, 33.035372],
    [-22.457882, 33.035372],


  this.map.fitBounds(
        new mapboxgl.LngLatBounds(
          [this.bounds[0], this.bounds[1]],
          [this.bounds[2], this.bounds[3]]
        ),
        { padding: 30 }
      );
      */
    });
  }

  addCountryLayers(): void {
    if (!this.map.getSource('country-boundaries')) {
      this.map.addSource('country-boundaries', {
        type: 'vector',
        url: 'mapbox://mapbox.country-boundaries-v1',
      });
      this.map.addLayer({
        id: 'boundary-layer',
        source: 'country-boundaries',
        'source-layer': 'country_boundaries',
        type: 'fill',
        filter: ['==', ['get', 'disputed'], 'false'],
        paint: {
          'fill-color': 'rgba(255, 255, 255, 0.0)',
          'fill-outline-color': 'rgba(255, 255, 255, 0.9)',
        },
      });
      this.map.addSource('streets', {
        url: 'mapbox://mapbox.mapbox-streets-v8',
        type: 'vector',
      });
      this.map.addLayer({
        id: 'country-names',
        source: 'streets',
        'source-layer': 'place_label',
        type: 'symbol',
        paint: {
          'text-color': 'white',
          'text-opacity': 1.0,
          'text-halo-color': 'white',
          'text-halo-width': 0.2,
          'text-halo-blur': 0.5,
        },
        layout: {
          'text-field': ['get', 'name_' + this.language],
        },
        filter: ['==', 'type', 'country'],
      });
    }
  }
  addSatLayers(): void {
    this.addCountryLayers();
    this.satLayers = [];
    for (let i = 0; i < this.satData.length; i++) {
      this.map.addSource('sat-' + i, {
        url: this.satData[i].url,
        coordinates: this.coordinates,
        type: 'image',
      });
      const satLayer: mapboxgl.AnyLayer = {
        id: 'sat-' + i,
        source: 'sat-' + i,
        type: 'raster',
        paint: {
          'raster-opacity': 0.8,
          'raster-fade-duration': 0,
          'raster-opacity-transition': { delay: 0, duration: 0 },
        },
        layout: {
          visibility: i < this.satData.length - 1 ? 'none' : 'visible',
        },
      };
      this.map.addLayer(satLayer, 'boundary-layer');
      this.satLayers.push(satLayer);
    }
    this.setTimeStampControl(this.satData.length - 1);
  }

  removeSatLayers(): void {
    for (const satLayer of this.satLayers) {
      this.map.removeLayer(satLayer.id);
      this.map.removeSource(satLayer.id);
    }
    this.satLayers = [];
  }

  setTimeStep(index: number): void {
    if (!this.map) {
      return;
    }
    for (let i = 0; i < this.satData.length; i++) {
      const layer = 'sat-' + i;
      if (this.map.getLayer(layer) !== undefined) {
        this.map.setLayoutProperty(
          layer,
          'visibility',
          i === index ? 'visible' : 'none'
        );
      }
    }
    this.setTimeStampControl(index);
  }

  setTimeStampControl(index: number): void {
    if (this.timeStampControl) {
      this.map.removeControl(this.timeStampControl);
    }
    const text = moment(this.satData[index].dtg).format('dd DD.MM. HH:mm');
    this.timeStampControl = new TimeStampControl(text);
    this.map.addControl(this.timeStampControl, 'top-left');
  }

  getSatData(
    satType: string,
    hours: number,
    reload = false
  ): Promise<SatelliteData> {
    return new Promise(resolve => {
      if (this.satData !== undefined && this.satData.length > 0 && !reload) {
        resolve(this.satData);
      } else {
        this.fetchSatImages(satType, hours).subscribe(
          data => {
            this.satData = data;
            resolve(this.satData);
          },
          error => console.error(error)
        );
      }
    });
  }

  fetchSatImages(satType: string, hours: number): Observable<SatelliteData> {
    const startDtg = moment()
      .utc()
      .subtract(hours, 'hours')
      .format('YYYY-MM-DDTHH:mm:00');
    const endDtg = moment().utc().format('YYYY-MM-DDTHH:mm:ss');
    const fullUrl =
      environment.url.bffV3 +
      '/satellite/' +
      satType +
      '?startDtg=' +
      startDtg +
      'Z&endDtg=' +
      endDtg +
      'Z';
    return this.http.get<SatelliteData>(fullUrl);
  }

  private setSelectLayerControl() {
    this.map.addControl(
      new CustomIconControl(
        ['far', 'layer-group'],
        'click',
        onSelectLayer.bind(
          this,
          this.dialog,
          this.map,
          this.localStorageId,
          () => {
            this.removeSatLayers();
          },
          () => {
            this.addSatLayers();
          }
        )
      )
    );
  }
}

class TimeStampControl {
  map: mapboxgl.Map;
  container: HTMLDivElement;
  text: string;
  constructor(text: string) {
    this.text = text;
  }
  onAdd(map): HTMLDivElement {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'dtg-control';
    this.container.textContent = this.text;
    return this.container;
  }
  onRemove(): void {
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  }
}

export type SatelliteData = Array<SingleSatelliteDataItem>;

export interface SingleSatelliteDataItem {
  dtg: string;
  url: string;
}
